import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  ASSET_CONDITIONS,
  assetsApi,
  type AssetCondition,
  type AssetDetail,
  type AssetSummary,
  type DismountAllResult,
  type DismountRequest,
} from "../api/assets";
import { structureApi, type PositionDetail } from "../api/structure";
import { locationsApi } from "../api/locations";
import type { ApiError } from "../api/client";
import { formatApiError } from "../lib/apiError";
import { localDateTimeInputToIso, toLocalDateTimeInput } from "../lib/dates";
import { findFlatLocation, flattenLocations, type FlatLocation } from "../lib/locationsFlat";
import { pathLabel } from "../lib/paths";
import { conditionLabel } from "./ConditionChip";
import { DateTimeInput } from "./DateInput";
import { LocationDestinationFields, missingReturnDate, returnDateFor } from "./LocationDestinationFields";
import { Modal } from "./Modal";
import { Select, type SelectOption } from "./Select";

const NO_CHANGE = "";

type Props = { onClose: () => void } & (
  /** One asset (karton or the structure card). */
  | { asset: AssetSummary; position?: undefined; count?: undefined; onDone: (detail: AssetDetail) => void }
  /** Whole assembly: every mounted asset in the position's sub-tree (`POST /positions/{id}/dismount-all`). */
  | { position: PositionDetail; count: number; asset?: undefined; onDone: (result: DismountAllResult) => void }
);

/**
 * Dismount dialog (contract §3/§4). The destination location is mandatory —
 * an asset never exists without a physical place. Choosing a location whose
 * kind behaves as EXTERNAL_SERVICE reveals the mandatory expected-return date (condition becomes
 * NA_SERVISU server-side, so the condition select is hidden then).
 */
export function DismountModal(props: Props) {
  const { t } = useTranslation();
  const { onClose } = props;
  const [flat, setFlat] = useState<FlatLocation[] | null>(null);
  const [locationId, setLocationId] = useState("");
  const [locationNote, setLocationNote] = useState("");
  const [expectedReturnAt, setExpectedReturnAt] = useState("");
  const [newCondition, setNewCondition] = useState<string>(NO_CHANGE);
  const [occurredAt, setOccurredAt] = useState(() => toLocalDateTimeInput());
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    locationsApi.tree()
      .then((tree) => { if (!cancelled) setFlat(flattenLocations(tree)); })
      .catch((err: ApiError) => { if (!cancelled) { setFlat([]); setError(err.message); } });
    return () => { cancelled = true; };
  }, []);

  const isService = findFlatLocation(flat ?? [], locationId)?.kindBehavior === "EXTERNAL_SERVICE";

  const conditionOptions = useMemo<SelectOption<string>[]>(
    () => [
      { value: NO_CHANGE, label: t("dismount.conditionUnchanged", "— bez promene —") },
      ...ASSET_CONDITIONS.filter((c) => c !== "NA_SERVISU").map((c) => ({ value: c, label: conditionLabel(t, c) })),
    ],
    [t],
  );

  const whole = props.position !== undefined;
  const canSubmit = !!locationId && !missingReturnDate(flat, locationId, expectedReturnAt) && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    const body: DismountRequest = {
      toLocationId: locationId,
      locationNote: locationNote.trim() || null,
      newCondition: !isService && newCondition ? (newCondition as AssetCondition) : null,
      expectedReturnAt: returnDateFor(flat, locationId, expectedReturnAt),
      occurredAt: localDateTimeInputToIso(occurredAt),
      note: note.trim() || null,
    };
    setSubmitting(true);
    try {
      if (props.position) {
        props.onDone(await structureApi.dismountAll(props.position.id, body));
      } else {
        props.onDone(await assetsApi.dismount(props.asset.id, body));
      }
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "NOT_MOUNTED") setError(t("dismount.error.notMounted", "Asset nije montiran."));
      else if (ae.code === "NOTHING_MOUNTED") setError(t("dismount.error.nothingMounted", "U ovoj grani nema montiranih asseta."));
      else setError(formatApiError(ae));
    } finally {
      setSubmitting(false);
    }
  }

  const title = whole ? t("dismount.titleAll", "Demontaža celog sklopa") : t("dismount.title", "Demontaža asseta");
  const subtitle = props.position
    ? t("dismount.subAll", "{{name}} — {{count}} montiranih direktno u sklopu (pod-sklopovi idu takođe)", { name: pathLabel(props.position.path, props.position.name), count: props.count })
    : t("dismount.subAsset", "{{tag}} · {{type}} — sa pozicije {{pos}}", {
      tag: props.asset.tag, type: props.asset.typeName, pos: pathLabel(props.asset.positionPath, props.asset.positionName),
    });

  return (
    <Modal title={title} subtitle={subtitle} onClose={onClose} width={640}>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {props.position && (
          <div className="note-intervention" role="note">
            <div className="src">{t("dismount.allNoticeTitle", "Grupna akcija")}</div>
            {t("dismount.allNotice", "Svi montirani asseti iz „{{name}}“ i svih pod-pozicija ({{count}} direktno u sklopu) idu na istu lokaciju kao jedan događaj (batch). Struktura pozicija ostaje.", { count: props.count, name: props.position.name })}
          </div>
        )}
        <LocationDestinationFields
          flat={flat}
          label={t("dismount.toLocation", "Kuda ide (fizička lokacija)")}
          locationId={locationId}
          onLocationChange={setLocationId}
          locationNote={locationNote}
          onLocationNoteChange={setLocationNote}
          expectedReturnAt={expectedReturnAt}
          onExpectedReturnAtChange={setExpectedReturnAt}
        />
        {isService ? (
          <div className="muted" style={{ fontSize: 12.5 }}>{t("dismount.serviceHint", "Odredište je spoljni servis — stanje postaje „Na servisu“.")}</div>
        ) : (
          <label className="field">
            <span>{t("dismount.newCondition", "Novo stanje")}</span>
            <Select<string> value={newCondition} options={conditionOptions} onChange={setNewCondition} ariaLabel={t("dismount.newCondition", "Novo stanje")} />
          </label>
        )}
        <div className="form-grid">
          <label className="field">
            <span>{t("assets.field.occurredAt", "Datum i vreme")}</span>
            <DateTimeInput value={occurredAt} max={toLocalDateTimeInput()} onChange={setOccurredAt} />
          </label>
        </div>
        <label className="field">
          <span>{t("assets.field.note", "Napomena")}</span>
          <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} placeholder={t("dismount.notePlaceholder", "npr. Pregoreo namotaj")} />
        </label>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn lg" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary lg" disabled={!canSubmit}>
            {submitting
              ? t("action.saving", "Čuvanje…")
              : props.position
                ? t("dismount.submitAll", "Demontiraj ceo sklop")
                : t("dismount.submit", "Demontiraj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
